"use client";

import { COLOR, MeasureArrow, IllustrationCard } from "./_shared";
import { useDocLocale } from "@/components/shared/docs-blocks";
import type { Locale } from "@/lib/i18n";

// ── Return illustrations ────────────────────────────────────────────

const STRINGS: Record<Locale, Record<string, string>> = {
  ja: {
    capitalTitle: "キャピタルゲイン",
    capitalDescription: "買値より高く売れた差額がリターンになる",
    incomeTitle: "インカムゲイン",
    incomeDescription: "保有中に受け取る配当や利息",
    buyPrice: "買値",
    sellPrice: "売値",
    difference: "差額",
    dividendInterest: "配当・利息",
  },
  en: {
    capitalTitle: "Capital gain",
    capitalDescription: "The gap between your buy price and a higher sell price is the return",
    incomeTitle: "Income gain",
    incomeDescription: "Dividends and interest received while you hold",
    buyPrice: "Buy",
    sellPrice: "Sell",
    difference: "Gain",
    dividendInterest: "Dividends & interest",
  },
};

const VW = 240;
const VH = 120;

function CapitalGainIllustration() {
  const t = STRINGS[useDocLocale()];
  const c = COLOR.emerald;
  return (
    <svg viewBox={`0 0 ${VW} ${VH}`} className="w-full">
      <line x1={20} y1={28} x2={220} y2={28} stroke="currentColor" strokeWidth="0.5" strokeOpacity={0.1} />
      <line x1={20} y1={86} x2={220} y2={86} stroke="currentColor" strokeWidth="0.5" strokeOpacity={0.1} />
      <path
        d="M 24 80 Q 70 72, 100 58 Q 140 40, 180 32 Q 200 28, 216 26"
        fill="none"
        stroke={c}
        strokeWidth="2.5"
        strokeLinecap="round"
      />
      <rect x={16} y={84} width={38} height={18} rx={5} fill={c} fillOpacity={0.15} stroke={c} strokeWidth="0.8" />
      <text x={35} y={96} textAnchor="middle" fontSize="9" fill={c} fontWeight="600">
        {t.buyPrice}
      </text>
      <rect x={188} y={10} width={38} height={18} rx={5} fill={c} fillOpacity={0.15} stroke={c} strokeWidth="0.8" />
      <text x={207} y={22} textAnchor="middle" fontSize="9" fill={c} fontWeight="600">
        {t.sellPrice}
      </text>
      <MeasureArrow x1={228} y1={26} x2={228} y2={80} color={c} label={t.difference} />
    </svg>
  );
}

function IncomeIllustration() {
  const t = STRINGS[useDocLocale()];
  const c = COLOR.emerald;
  const bars = [36, 76, 116, 156, 196];
  return (
    <svg viewBox={`0 0 ${VW} ${VH}`} className="w-full">
      <line x1={20} y1={90} x2={220} y2={90} stroke="currentColor" strokeWidth="0.5" strokeOpacity={0.1} />
      <path
        d="M 20 64 Q 60 60, 90 56 Q 130 52, 170 48 Q 200 45, 220 42"
        fill="none"
        stroke={c}
        strokeWidth="2"
        strokeLinecap="round"
      />
      {bars.map((x, i) => (
        <rect
          key={x}
          x={x - 9}
          y={60}
          width={18}
          height={30}
          rx={4}
          fill={c}
          fillOpacity={0.12 + i * 0.04}
          stroke={c}
          strokeWidth="0.8"
        />
      ))}
      <text x={VW / 2} y={108} textAnchor="middle" fontSize="8.5" fill={c} fontWeight="600">
        {t.dividendInterest}
      </text>
    </svg>
  );
}

// ── Export ───────────────────────────────────────────────────────────

export function DocReturnTypesDiagram() {
  const t = STRINGS[useDocLocale()];
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      <IllustrationCard title={t.capitalTitle} description={t.capitalDescription}>
        <CapitalGainIllustration />
      </IllustrationCard>
      <IllustrationCard title={t.incomeTitle} description={t.incomeDescription}>
        <IncomeIllustration />
      </IllustrationCard>
    </div>
  );
}
